import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";

import {
  ASSEMBLY_WORK_ORDERS_QUERY_KEY,
  createAssemblyWorkOrder,
  fetchAssemblyKinds,
  fetchAssemblyMaterialPlanDiff,
  type AssemblyKind,
  type MaterialPlanMode,
} from "./assemblyApi";

const MODAL_HELP =
  "Creates a hub work order at SGS for serialized assembly jobs. Choose scratch build (full output BOM) or " +
  "rebuild from an existing TLA (diff-only BOM). Scan PCBA serials on the work order detail after assembly.";

const MATERIAL_PLAN_HELP =
  "Scratch build stamps the full hub BOM for the output SKU. Rebuild from existing stamps only the components " +
  "that differ between the input TLA and the output SKU. Mid-job BOM changes use work order deviations.";

export function CreateAssemblyWorkOrderModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (buildOrderId: number) => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const kindsQuery = useQuery({
    queryKey: ["manufacturing", "assembly", "kinds"],
    queryFn: fetchAssemblyKinds,
    enabled: open,
    staleTime: 60_000,
  });

  const kinds = kindsQuery.data?.kinds ?? [];
  const [assemblyKindId, setAssemblyKindId] = useState("");
  const [materialPlanMode, setMaterialPlanMode] = useState<MaterialPlanMode>("scratch");
  const [sourceSku, setSourceSku] = useState("");
  const [quantityText, setQuantityText] = useState("");
  const [reference, setReference] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedKind: AssemblyKind | null =
    kinds.find((kind) => kind.id === assemblyKindId) ?? kinds[0] ?? null;

  useEffect(() => {
    if (!open) {
      setAssemblyKindId("");
      setMaterialPlanMode("scratch");
      setSourceSku("");
      setQuantityText("");
      setReference("");
      setErrorMessage(null);
      return;
    }
    if (kinds.length > 0 && assemblyKindId === "") {
      setAssemblyKindId(kinds[0]!.id);
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, kinds, assemblyKindId]);

  useEffect(() => {
    if (selectedKind == null) return;
    if (materialPlanMode === "rebuild_from_existing" && sourceSku === "") {
      setSourceSku(selectedKind.sourceSkus[0] ?? "");
    }
    if (materialPlanMode === "scratch") {
      setSourceSku("");
    }
  }, [selectedKind, materialPlanMode, sourceSku]);

  const diffQuery = useQuery({
    queryKey: [
      "manufacturing",
      "assembly",
      "material-plan-diff",
      selectedKind?.id,
      selectedKind?.outputSku,
      sourceSku,
      materialPlanMode,
    ],
    queryFn: () =>
      fetchAssemblyMaterialPlanDiff({
        targetSku: selectedKind!.outputSku,
        sourceSku: materialPlanMode === "rebuild_from_existing" ? sourceSku : null,
        assemblyKind: selectedKind!.id,
        materialPlanMode,
      }),
    enabled:
      open &&
      selectedKind != null &&
      (materialPlanMode === "scratch" ||
        (materialPlanMode === "rebuild_from_existing" && sourceSku.trim() !== "")),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: createAssemblyWorkOrder,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ASSEMBLY_WORK_ORDERS_QUERY_KEY });
      onCreated?.(result.work_order.id);
      onClose();
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof ApiError ? error.message : "Could not create assembly work order.");
    },
  });

  if (!open) {
    return null;
  }

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setErrorMessage(null);
    if (selectedKind == null) {
      setErrorMessage("No assembly kinds are configured.");
      return;
    }
    const quantity = Number.parseInt(quantityText.trim(), 10);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setErrorMessage("Quantity must be a positive integer.");
      return;
    }
    const trimmedReference = reference.trim();
    if (trimmedReference === "") {
      setErrorMessage("Reference is required.");
      return;
    }
    if (materialPlanMode === "rebuild_from_existing" && sourceSku.trim() === "") {
      setErrorMessage("Select an input TLA SKU for rebuild jobs.");
      return;
    }
    createMutation.mutate({
      assemblyKind: selectedKind.id,
      quantity,
      manufacturerReferenceNumber: trimmedReference,
      comments: `${selectedKind.label} at SGS`,
      materialPlanMode,
      sourceSku: materialPlanMode === "rebuild_from_existing" ? sourceSku.trim() : null,
    });
  };

  return createPortal(
    <div
      {...hubModalOverlayProps(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-assembly-work-order-modal-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <form
        className="my-4 w-full max-w-2xl rounded-lg border border-slate-200 bg-white shadow-xl"
        onSubmit={handleSubmit}
      >
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="create-assembly-work-order-modal-title" className="text-lg font-semibold text-slate-900">
              New assembly work order
            </h2>
            <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
          </div>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-5">
          <div className="space-y-2">
            <Label htmlFor="assembly-create-kind">Assembly kind</Label>
            <select
              id="assembly-create-kind"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
              value={assemblyKindId}
              disabled={kindsQuery.isLoading || kinds.length === 0}
              onChange={(event) => setAssemblyKindId(event.target.value)}
            >
              {kinds.length === 0 ? (
                <option value="">No assembly kinds available</option>
              ) : (
                kinds.map((kind) => (
                  <option key={kind.id} value={kind.id}>
                    {kind.label}
                  </option>
                ))
              )}
            </select>
            {selectedKind ? (
              <p className="text-xs text-slate-600">{selectedKind.description}</p>
            ) : null}
          </div>

          <fieldset className="space-y-2">
            <legend className="flex items-center gap-2 text-sm font-medium text-slate-900">
              Material plan
              <HoverHintWithGlyph hoverHint={MATERIAL_PLAN_HELP} size="sm" />
            </legend>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-800">
                <input
                  type="radio"
                  name="assembly-material-plan"
                  checked={materialPlanMode === "scratch"}
                  onChange={() => setMaterialPlanMode("scratch")}
                />
                Scratch build
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-800">
                <input
                  type="radio"
                  name="assembly-material-plan"
                  checked={materialPlanMode === "rebuild_from_existing"}
                  onChange={() => setMaterialPlanMode("rebuild_from_existing")}
                />
                Rebuild from existing TLA
              </label>
            </div>
          </fieldset>

          {materialPlanMode === "rebuild_from_existing" && selectedKind != null ? (
            <div className="space-y-2">
              <Label htmlFor="assembly-create-source-sku">Input TLA SKU</Label>
              <select
                id="assembly-create-source-sku"
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
                value={sourceSku}
                onChange={(event) => setSourceSku(event.target.value)}
              >
                {selectedKind.sourceSkus.map((sku) => (
                  <option key={sku} value={sku}>
                    {sku}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {diffQuery.data != null ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              {materialPlanMode === "scratch" ? (
                <p className="text-slate-700">
                  {diffQuery.data.target_bom_resolved
                    ? `Full hub BOM for ${diffQuery.data.target_sku} will be stamped on the work order.`
                    : (diffQuery.data.note ??
                      `Output SKU ${diffQuery.data.target_sku} has no hub BOM lines yet — create may fail until the template is linked.`)}
                </p>
              ) : diffQuery.data.lines.length === 0 ? (
                <p className="text-amber-800">
                  {diffQuery.data.note ?? "No component differences were found between source and target BOMs."}
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-slate-600">
                    Diff-only BOM ({diffQuery.data.lines.length} variable
                    {diffQuery.data.lines.length === 1 ? "" : "s"}) from {diffQuery.data.source_sku} →{" "}
                    {diffQuery.data.target_sku}
                  </p>
                  {diffQuery.data.note ? <p className="text-xs text-amber-800">{diffQuery.data.note}</p> : null}
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="text-slate-500">
                        <th className="pb-1 pr-2 font-medium">Variable</th>
                        <th className="pb-1 pr-2 font-medium">Source</th>
                        <th className="pb-1 font-medium">Target</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffQuery.data.lines.map((line) => (
                        <tr key={line.variable_name} className="font-mono text-slate-800">
                          <td className="py-0.5 pr-2">{line.variable_name}</td>
                          <td className="py-0.5 pr-2">{line.source_component_sku ?? "—"}</td>
                          <td className="py-0.5">{line.target_component_sku ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : diffQuery.isLoading ? (
            <p className="text-xs text-slate-500">Loading material plan preview…</p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="assembly-create-qty">Quantity</Label>
              <Input
                id="assembly-create-qty"
                type="number"
                min={1}
                value={quantityText}
                onChange={(event) => setQuantityText(event.target.value)}
                placeholder="15"
              />
            </div>
            <div className="space-y-2 sm:col-span-1">
              <Label htmlFor="assembly-create-ref">Reference</Label>
              <Input
                id="assembly-create-ref"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                placeholder="Sales order or batch reference"
              />
            </div>
          </div>

          {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" isLoading={createMutation.isPending} disabled={kinds.length === 0}>
            <Plus className="h-4 w-4" aria-hidden />
            Create work order
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
